import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { MaskRegion } from '@editor/interfaces/maskRegion'
import { Cuboid } from '@editor/interfaces/shape'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { generateCuboidUniforms } from '@editor/services/Points'
import { useAppSelector } from '@editor/store/app'
import { HEADER_TEXELS, MAX_REGIONS, MeshTilesManager, TEXELS_PER_REGION } from '@infraloop/common'
import { useMatches } from 'react-router'
import { ClampToEdgeWrapping, DataTexture, FloatType, NearestFilter, RGBAFormat } from 'three'

const useMaskRegion = (tilesManager: MeshTilesManager | null) => {
  // Route matching to check current page
  const matches = useMatches()
  const isEditorPage = matches.some((row) => row.id === 'project-editor')

  // Store
  const workingMask = useAppSelector((state) => state.maskPCD.workingMask)
  const savingMask = useAppSelector((state) => state.maskPCD.savingMask)
  const maskRegions = useAppSelector((state) => state.maskPCD.regions)

  const selectedEditorTool = useAppSelector((state) => state.editor.selectedTool)
  const prevSelectedEditorTool = useAppSelector((state) => state.editor.prevSelectedTool)
  const selectedBpTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const prevSelectedBpTool = useAppSelector((state) => state.blueprint.editor.prevSelectedTool)

  const solidMeshTransparency = useAppSelector((state) =>
    isEditorPage ? state.editorSettings.display.mesh.opacity : state.solidMesh.transparency,
  )

  // Data texture to hold mask regions (1D texture layout)
  const totalTexels = HEADER_TEXELS + MAX_REGIONS * TEXELS_PER_REGION
  const data = useRef(new Float32Array(totalTexels * 4))
  const [maskDataTexture] = useState(() => {
    const regionTexture = new DataTexture(data.current, totalTexels, 1, RGBAFormat, FloatType)

    regionTexture.minFilter = NearestFilter
    regionTexture.magFilter = NearestFilter
    regionTexture.wrapS = ClampToEdgeWrapping
    regionTexture.wrapT = ClampToEdgeWrapping
    regionTexture.needsUpdate = true
    return regionTexture
  })

  // prettier-ignore
  // Calculate mask region uniforms for solid mesh (same logic as PCD)
  const activeMasks = useMemo(() => [
    ...maskRegions,
    ...(savingMask || []),
  ].filter((region) => !region.invisible), [maskRegions , savingMask])

  // Force show outside of masks when in mask editing mode to provide better visual feedback to users.
  const forceShowOutside = useMemo(() => {
    if (isEditorPage) {
      return (
        EDITOR_TOOLS.PCD_TRIM_CUBOID === selectedEditorTool ||
        (EDITOR_TOOLS.FOCUS === selectedEditorTool && prevSelectedEditorTool === EDITOR_TOOLS.PCD_TRIM_CUBOID)
      )
    }

    return (
      selectedBpTool === BlueprintEditorTools.MaskPCD ||
      (selectedBpTool === BlueprintEditorTools.Selector && prevSelectedBpTool === BlueprintEditorTools.MaskPCD)
    )
  }, [selectedEditorTool, prevSelectedEditorTool, selectedBpTool, prevSelectedBpTool, isEditorPage])

  /**
   * Update mask region data texture based on current mask regions in store.
   * This will trigger shader update to apply masking on solid mesh.
   */
  const updateMaskDataTexture = useCallback(
    (extraMasks: MaskRegion[] = []) => {
      if (!tilesManager) return

      const masks = [...activeMasks, ...extraMasks]
      const maskRegionUniforms = masks.map(generateCuboidUniforms)

      // First texel is reserved for header information
      data.current[0] = maskRegionUniforms.length // Region count
      data.current[1] = forceShowOutside ? 1 : 0 // Force show outside of masks
      data.current[2] = 0
      data.current[3] = 0

      maskRegionUniforms.forEach((region, index) => {
        const offset = HEADER_TEXELS * 4 + index * TEXELS_PER_REGION * 4

        data.current.set([solidMeshTransparency, 0, 0, 0], offset + 0) // 4 floats (1 texel) - region header
        data.current.set(region.modelMatrix.toArray(), offset + 4) // 16 floats (4 texels)
        data.current.set([region.min.x, region.min.y, region.min.z, 0], offset + 20) // 4 floats (1 texel)
        data.current.set([region.max.x, region.max.y, region.max.z, 0], offset + 24) // 4 floats (1 texel)
      })

      maskDataTexture.needsUpdate = true

      // Update tiles as well to activate/deactivate based on available masks
      tilesManager.masks = forceShowOutside ? [] : maskRegionUniforms
      tilesManager.toggleTileBasedOnMasks()
    },
    [activeMasks, forceShowOutside, solidMeshTransparency, maskDataTexture, tilesManager],
  )

  /**
   * Change solid mesh transparency and apply masking
   */
  useEffect(() => {
    updateMaskDataTexture(workingMask ? [workingMask] : [])
    tilesManager?.tick() // Force update to apply changes immediately
  }, [workingMask, tilesManager, updateMaskDataTexture])

  /**
   * Handle working mask changes - update data texture with new region data.
   *
   * The actual change actually comes from document event 'editor:working-cuboid:change'
   * but we're listening to workingMask as well to note when it's cleared (set to null)
   * so we can update the texture accordingly.
   */
  useEffect(() => {
    const handleWorkingMaskChange = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      const updatedCuboid = event.detail as Cuboid
      const updatedRegion: MaskRegion = {
        region_id: 'working-mask',
        ...updatedCuboid,
      }

      updateMaskDataTexture([updatedRegion])
    }

    document.addEventListener('editor:working-cuboid:change', handleWorkingMaskChange)
    return () => {
      document.removeEventListener('editor:working-cuboid:change', handleWorkingMaskChange)
    }
  }, [updateMaskDataTexture])

  /**
   * Cleanup on unmount - dispose data texture to free GPU memory.
   */
  useEffect(
    () => () => {
      maskDataTexture.dispose()
    },
    [maskDataTexture],
  )

  return { forceShowOutside, maskDataTexture }
}

export default useMaskRegion
